import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export const THEMES = {
  'deep-ocean': {
    name: 'Deep Ocean',
    primary: '#0066cc',
    emoji: '🌊',
  },
  'forest-mint': {
    name: 'Forest Mint',
    primary: '#10b981',
    emoji: '🌿',
  },
  'midnight-purple': {
    name: 'Midnight Purple',
    primary: '#8b5cf6',
    emoji: '🌙',
  },
  'arctic-blue': {
    name: 'Arctic Blue',
    primary: '#3b82f6',
    emoji: '❄️',
  },
  'rose-gold': {
    name: 'Rose Gold',
    primary: '#f43f5e',
    emoji: '🌹',
  },
};

const CUSTOM_STORAGE_KEY = 'speedsplit-custom-theme';

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

const darken = (hex, amount = 0.15) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.max(0, Math.round(r * (1 - amount)))},${Math.max(0, Math.round(g * (1 - amount)))},${Math.max(0, Math.round(b * (1 - amount)))})`;
};

const applyCustomTheme = (primary, accent, useGradient) => {
  const el = document.documentElement;
  const resolvedAccent = accent || primary;
  el.setAttribute('data-theme', 'custom');
  el.style.setProperty('--primary', primary);
  el.style.setProperty('--accent', resolvedAccent);
  el.style.setProperty('--blob1', primary);
  el.style.setProperty('--blob2', resolvedAccent);
  el.style.setProperty('--glass-bg', `${primary}26`);
  el.style.setProperty('--glass-bg-dark', darken(primary, 0.7));
  el.style.setProperty('--bg-base', darken(primary, 0.82));
  el.style.setProperty('--text-primary', '#ffffff');
  el.style.setProperty('--text-secondary', 'rgba(255,255,255,0.75)');
  el.style.setProperty('--text-muted', 'rgba(255,255,255,0.5)');
  el.style.setProperty('--glass-border', 'rgba(255,255,255,0.18)');
  el.style.setProperty('--glass-shadow', '0 8px 32px rgba(0,0,0,0.35)');
  if (useGradient) {
    el.style.setProperty('--card-header-bg', `linear-gradient(135deg, ${primary}, ${resolvedAccent})`);
  } else {
    el.style.setProperty('--card-header-bg', primary);
  }
};

const clearCustomTheme = () => {
  const el = document.documentElement;
  const props = ['--primary','--accent','--blob1','--blob2','--glass-bg','--glass-bg-dark','--bg-base','--text-primary','--text-secondary','--text-muted','--glass-border','--glass-shadow','--card-header-bg'];
  props.forEach(p => el.style.removeProperty(p));
};

const ThemeContext = createContext();

const readCssVars = () => {
  const style = getComputedStyle(document.documentElement);
  return {
    primary: style.getPropertyValue('--primary').trim(),
    accent: style.getPropertyValue('--accent').trim(),
    textPrimary: style.getPropertyValue('--text-primary').trim(),
    glassBorder: style.getPropertyValue('--glass-border').trim(),
  };
};

export const ThemeProvider = ({ children }) => {
  const savedCustom = (() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_STORAGE_KEY)); } catch { return null; }
  })();

  const [activeTheme, setActiveTheme] = useState(() => {
    const saved = localStorage.getItem('speedsplit-theme') || 'midnight-purple';
    if (saved === 'custom' && savedCustom) {
      applyCustomTheme(savedCustom.primary, savedCustom.accent, savedCustom.useGradient);
    } else {
      document.documentElement.setAttribute('data-theme', saved);
    }
    return saved;
  });

  const [customColors, setCustomColors] = useState(savedCustom || { primary: '#8b5cf6', accent: '#a78bfa', useGradient: false });
  const [themeColors, setThemeColors] = useState(() => readCssVars());

  useEffect(() => {
    if (activeTheme === 'custom') {
      applyCustomTheme(customColors.primary, customColors.accent, customColors.useGradient);
    } else {
      clearCustomTheme();
      document.documentElement.setAttribute('data-theme', activeTheme);
    }
    localStorage.setItem('speedsplit-theme', activeTheme);
    setThemeColors(readCssVars());

    const primary = activeTheme === 'custom' ? customColors.primary : (THEMES[activeTheme]?.primary || '#8b5cf6');
    const metaTheme = document.querySelector("meta[name='theme-color']");
    if (metaTheme) metaTheme.content = primary;

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="20" fill="${primary}"/><g transform="translate(2.5,27.5)"><g transform="translate(0,65) scale(0.1,-0.1)" fill="#ffffff" stroke="none"><path d="M50 561 c0 -23 5 -52 12 -64 6 -12 12 -36 14 -53 2 -32 30 -84 44 -84 4 0 11 -11 14 -24 3 -13 17 -31 31 -40 14 -9 25 -25 25 -36 0 -11 12 -27 28 -37 15 -10 33 -24 41 -31 33 -32 111 -44 111 -17 0 9 -9 15 -24 15 -13 0 -32 3 -42 7 -15 5 -10 10 22 24 l39 18 -51 -5 c-50 -6 -94 6 -94 26 0 6 28 11 63 11 l62 2 -73 12 c-53 8 -80 18 -95 35 l-22 23 25 -7 c14 -3 52 -8 85 -11 65 -5 65 -5 -63 31 -55 15 -92 43 -92 69 0 13 4 14 21 4 28 -15 161 -60 166 -56 1 2 -32 19 -74 37 -79 35 -143 82 -143 105 0 18 220 -97 243 -127 10 -13 33 -47 50 -77 31 -53 57 -76 57 -50 0 7 -7 20 -15 29 -9 10 -26 44 -38 76 l-22 59 -122 60 c-68 34 -136 74 -153 90 l-30 29 0 -43z"/><path d="M490 533 c-49 -25 -80 -76 -80 -129 0 -59 36 -98 131 -143 77 -37 96 -64 69 -97 -26 -30 -100 -5 -100 34 0 8 -18 12 -50 12 -45 0 -50 -2 -50 -22 0 -33 35 -85 72 -108 40 -24 126 -27 168 -5 45 23 74 71 74 126 1 66 -24 96 -120 144 -66 33 -80 44 -82 67 -6 53 61 59 88 8 9 -16 21 -20 61 -20 51 0 51 0 45 28 -23 95 -140 150 -226 105z"/><path d="M1058 588 c-12 -18 -62 -48 -150 -92 l-133 -64 -27 -58 c-21 -46 -24 -62 -16 -78 10 -19 14 -14 42 44 l31 65 120 62 c66 35 121 63 123 63 1 0 2 -7 2 -14 0 -21 -54 -58 -142 -99 -43 -20 -78 -38 -78 -41 0 -7 128 38 168 58 25 13 32 14 32 3 0 -31 -57 -67 -145 -92 -84 -23 -86 -25 -35 -20 30 3 71 8 90 11 l35 6 -22 -22 c-15 -17 -43 -27 -95 -35 l-73 -12 63 -2 c65 -1 79 -14 37 -33 -18 -8 -38 -8 -70 -1 -25 6 -45 6 -45 2 0 -5 4 -9 9 -9 5 0 22 -5 38 -11 22 -8 24 -12 13 -19 -8 -6 -27 -10 -42 -10 -19 0 -28 -5 -28 -16 0 -14 8 -16 41 -11 50 7 118 53 138 93 8 16 25 37 38 47 13 10 23 26 23 36 0 10 11 28 25 41 14 13 27 39 31 59 3 20 10 48 15 62 6 14 9 46 7 70 l-3 43 -17 -26z"/></g></g></svg>`;
    const link = document.querySelector("link[rel~='icon'][type='image/svg+xml']");
    if (link) link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, [activeTheme, customColors]);

  const switchTheme = (themeKey) => {
    if (THEMES[themeKey]) setActiveTheme(themeKey);
  };

  const applyCustom = useCallback((primary, accent, useGradient) => {
    const next = { primary, accent, useGradient };
    setCustomColors(next);
    localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(next));
    setActiveTheme('custom');
  }, []);

  return (
    <ThemeContext.Provider value={{ activeTheme, switchTheme, applyCustom, customColors, themes: THEMES, themeColors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
